import React, { useEffect } from "react";
import stylesPc from "./SelectTag.module.css";
import stylesSp from "./SelectTag_SP.module.css";
import styleIpad from "./SelectTag_IP.module.css";
import { IsDevice } from "../../components/function/useDeviceFlag";
import {
    FormControl,
    FilledInput,
    InputAdornment,
    createStyles,
    makeStyles,
    Theme,
    Backdrop,
    Collapse,
    Modal,
    createTheme,
    ThemeProvider,
} from "@material-ui/core";
import Search from "@material-ui/icons/Search";
import { getTagList, createNewTag, ResetDuplicateError } from "../../common/video/VideoActions";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../root/RootReducer";
import PuiFloatButton from "../../components/elements/PuiFloatButton";
import PuiTypography from "../../components/elements/PuiTypography";
import { useInitFlag } from "../../components/function/useInitFlag";
import PuiMessage from "../../components/widgets/PuiMessage";
import MoreIcon from "../../assets/icon/MoreIcon";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        searchTitle: {
            fontFamily: "Yu Gothic",
            fontSize: "18px",
            color: "white",
            opacity: "0.8",
        },
        searchButton: {
            fontSize: "1rem",
            borderRadius: "5px",
            marginRight: "15px",
            backgroundColor: "var(--red)",
            "&:hover": {
                backgroundColor: "rgb(193, 0, 25)",
            },
        },
        newTagButton: {
            fontSize: "1rem",
            borderRadius: "5px",
            backgroundColor: "var(--red)",
            "&:hover": {
                backgroundColor: "rgb(193, 0, 25)",
            },
        },
        modal: {
            width: "400px",
            marginLeft: "calc((100vw - 800px) / 2 + 380px)",
            marginTop: "calc((100vh - 450px) / 2 + 330px)",
        },
        paper: {
            backgroundColor: theme.palette.background.paper,
        },
    })
);

interface SelectTagProps {
    /* モーダル表示切り替え */
    in: boolean;
    // モーダル閉じる
    onClose?: () => void;
    tagList: string[];
    setTagList: (tagList: string[]) => void;
    setIsSaved?: (flg: boolean) => void;
    isShowStudio?: boolean;
}

const theme = createTheme({
    palette: {
        secondary: {
            main: "#606060",
        },
    },
});

const SelectTag = React.forwardRef<HTMLDivElement, SelectTagProps>(function SelectTag(props, ref) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : IsDevice("ipad") ? styleIpad : stylesSp;

    const { onClose, tagList, setTagList, setIsSaved, isShowStudio } = props;
    const classes = useStyles();
    const dispatch = useDispatch();
    const [displaySkillTag, ChangeDisplaySkillTag] = React.useState<string[]>([]);
    const [searchSkill, ChangeSearchSkill] = React.useState<string[]>([]);
    const [isLimitTag, ChangeLimitTag] = React.useState(false);
    const [isLimitAddTag, ChangeLimitAddTag] = React.useState(false);
    const { tags, tagDuplicateError } = useSelector(({ video }: AppState) => video);
    const [open, setOpen] = React.useState(false);
    const [newTag, setNewTag] = React.useState<string>("");
    const [newTagList, setNewTagList] = React.useState<string[]>(tagList);
    const [searchKey, setSearchKey] = React.useState("");
    const [isShowError, setShowError] = useInitFlag();
    const [openUploadMessage, setOpenUploadMessage] = React.useState(false);

    const handleNewTagModal = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleConditionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKey(event.currentTarget.value.trim());
        const input = event.currentTarget.value.trim().split(/\s+/);
        if (input.length > 0 && input[0] !== "") {
            dispatch(getTagList(input));
            ChangeSearchSkill(input);
        } else {
            dispatch(getTagList([]));
            ChangeSearchSkill([]);
        }
        ChangeDisplaySkillTag([]);
    };

    const handleTagChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewTag(event.currentTarget.value);
    };

    const handleTagKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && (newTag || searchKey)) {
            handleNewTag();
        }
    };

    const handleAddTag = () => {
        if (onClose) {
            setTagList(newTagList);
            if (setIsSaved) {
                setIsSaved(false);
            }
            onClose();
        }
    };

    const closeErrorMessage = () => {
        setShowError(false);
        dispatch(ResetDuplicateError());
    };

    const handleNewTag = () => {
        const addKey = searchKey !== newTag && newTag !== "" ? newTag : searchKey;
        const input = searchKey.trim().split(/\s+/);
        if (input.length > 0 && input[0] !== "") {
            dispatch(createNewTag(addKey, input));
        } else {
            dispatch(createNewTag(addKey, []));
        }
        if (!newTagList.includes(addKey)) {
            setNewTagList([...newTagList, addKey]);
            setTagList([...newTagList, addKey]);
        }
        if (setIsSaved) {
            setIsSaved(false);
        }
        handleClose();
    };

    const handleCheck = (tag: string) => {
        let tempList = newTagList;
        if (newTagList.includes(tag)) {
            tempList = newTagList.filter((v) => v !== tag);
        } else {
            tempList = newTagList.concat(tag);
        }
        setNewTagList(tempList);
    };

    useEffect(() => {
        if (tagDuplicateError !== "") {
            setShowError(true);
        }
        // eslint-disable-next-line
    }, [tagDuplicateError]);

    useEffect(() => {
        dispatch(getTagList([]));
    }, [dispatch]);

    // 検索していないときは、新規追加したタグを合わせて表示させる。検索時はフィルター結果を表示させる
    useEffect(() => {
        ChangeDisplaySkillTag([]);
        const tmpTags: string[] = [];
        tags && tags.length > 0 && tags.map((row) => {
            // タグ表示を重複しないように
            if (!tmpTags.includes(row.tag.title)) {
                return tmpTags.push(row.tag.title);
            } else {
                return "";
            }
        });
        if (searchSkill === []) {
            ChangeDisplaySkillTag(tagList.concat(tmpTags.filter((tag) => !tagList.includes(tag))));
        } else {
            ChangeDisplaySkillTag(tmpTags);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tagList, tags]);

    // タグ数制限
    useEffect(() => {
        if (newTagList.length > 5) {
            ChangeLimitTag(true);
        } else {
            ChangeLimitTag(false);
        }
    }, [newTagList]);
    // 追加タグ数制限
    useEffect(() => {
        if (newTagList.length > 4) {
            ChangeLimitAddTag(true);
        } else {
            ChangeLimitAddTag(false);
        }
    }, [newTagList]);

    return (
        <>
            <ThemeProvider theme={theme}>
                {!IsDevice("desktop") && (
                    <div onClick={onClose} className={styles.closeButton}>
                        <img src={require("../../assets/image/closeFilter.svg")} alt="close" />
                    </div>
                )}
                <div
                    className={`${styles.root} ${isShowStudio ? styles.isShowStudio : ""}`}
                    style={{ backgroundColor: isShowStudio ? "#202327" : "white" }}
                >
                    <div
                        className={styles.conditionArea}
                        style={{ backgroundColor: isShowStudio ? "#202327" : "white" }}
                    >
                        <FormControl fullWidth variant="filled" color="secondary">
                            <FilledInput
                                id="searchInput"
                                style={{
                                    backgroundColor: isShowStudio ? "#FFFFFF0D" : "#9c9c9c",
                                }}
                                disableUnderline={true}
                                onChange={handleConditionChange}
                                placeholder={"タグを検索"}
                                startAdornment={<InputAdornment position="start"></InputAdornment>}
                                endAdornment={
                                    <ThemeProvider theme={theme}>
                                        <Search
                                            color="secondary"
                                            style={{
                                                color: isShowStudio ? "white" : "#202327",
                                                marginTop: "13px",
                                                marginRight: "12px",
                                            }}
                                        />
                                    </ThemeProvider>
                                }
                                autoComplete={"off"}
                                className={styles.searchInput}
                            />
                        </FormControl>
                        <div
                            className={`${styles.moreIcon} ${
                                isLimitAddTag ? styles.isLimitAddTag : ""
                            }`}
                            onClick={handleNewTagModal}
                            onMouseEnter={() => setOpenUploadMessage(true)}
                            onMouseLeave={() => setOpenUploadMessage(false)}
                        >
                            <MoreIcon />
                        </div>
                        {openUploadMessage && (
                            <div className={styles.hoverStatusButton}>
                                <PuiTypography color="black">新規タグを追加します</PuiTypography>
                            </div>
                        )}
                    </div>
                    <Modal
                        className={classes.modal}
                        open={open}
                        onClose={handleClose}
                        closeAfterTransition
                        BackdropComponent={Backdrop}
                        BackdropProps={{
                            timeout: 500,
                        }}
                    >
                        <Collapse in={open}>
                            <div className={classes.paper}>
                                <div className={styles.addTagArea}>
                                    <FormControl fullWidth variant="filled" color="secondary">
                                        <FilledInput
                                            id="searchInput"
                                            disableUnderline={true}
                                            onChange={handleTagChange}
                                            onKeyPress={handleTagKeyPress}
                                            placeholder={"追加するタグを入力してください"}
                                            startAdornment={
                                                <InputAdornment position="start"></InputAdornment>
                                            }
                                            endAdornment={
                                                <div
                                                    className={styles.AddTagButton}
                                                    onClick={handleNewTag}
                                                >
                                                    追加
                                                </div>
                                            }
                                            autoComplete={"off"}
                                            className={styles.searchInput}
                                        />
                                    </FormControl>
                                </div>
                            </div>
                        </Collapse>
                    </Modal>
                    <PuiMessage
                        open={isShowError}
                        type="error"
                        title={tagDuplicateError}
                        duration={5000}
                        onClose={closeErrorMessage}
                    />
                    <div className={styles.tagArea}>
                        {displaySkillTag.map((item) => (
                            <div
                                className={styles.tagCheckBox}
                                onClick={() => handleCheck(item)}
                                key={item}
                                style={{
                                    backgroundColor: isShowStudio
                                        ? newTagList.includes(item)
                                            ? "var(--studio-btn-bg)"
                                            : "#323335"
                                        : newTagList.includes(item)
                                            ? "var(--main)"
                                            : "var(--gray-61)",
                                }}
                            >
                                {item}
                            </div>
                        ))}
                    </div>
                    <div className={styles.btnArea}>
                        <div
                            style={{ display: isLimitTag ? "block" : "none" }}
                            className={styles.limitAttention}
                        >
                            <p
                                style={{
                                    color: isShowStudio ? "var(--studio-btn-bg)" : "var(--main)",
                                    fontSize: "15px",
                                }}
                            >
                                ※登録できるタグは5件までです
                            </p>
                        </div>
                        <div
                            className={`${styles.buttonGroup} ${
                                isLimitTag ? styles.isLimitTag : ""
                            }`}
                        >
                            <PuiFloatButton
                                children={["　完了　"]}
                                color="primary"
                                size="medium"
                                onClick={handleAddTag}
                                isActive={!isLimitTag}
                            ></PuiFloatButton>
                        </div>
                    </div>
                </div>
            </ThemeProvider>
        </>
    );
});

export default SelectTag;
